import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  actionCancelSubscription,
  actionReactivateSubscription,
} from "../store/actions/subscriptionAction";
import { actionGetFaxNumberSubscriptions } from "../store/actions/faxSubscriptionAction";

const skeletonStyle = `
  @keyframes fns-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  .fns-skel {
    border-radius: 6px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 600px 100%;
    animation: fns-shimmer 1.4s infinite linear;
    display: inline-block;
  }
`;

const Skel = ({ w, h = 14, rounded = false }) => (
  <span
    className="fns-skel"
    style={{
      width: w,
      height: h,
      borderRadius: rounded ? 999 : 6,
      display: "block",
    }}
  />
);

const SkeletonRows = () =>
  [1, 2, 3].map((i) => (
    <tr key={i} style={{ borderBottom: "1px solid #f4f5f7" }}>
      <td className="ps-4 py-3">
        <div className="d-flex align-items-center gap-2">
          <Skel w={36} h={36} rounded />
          <div>
            <Skel w={120} h={14} />
            <div style={{ marginTop: 6 }}><Skel w={60} h={11} /></div>
          </div>
        </div>
      </td>
      <td><Skel w={64} h={22} rounded /></td>
      <td><Skel w={56} h={14} /></td>
      <td><Skel w={88} h={14} /></td>
      <td><Skel w={100} h={22} rounded /></td>
      <td className="pe-4 text-end"><Skel w={80} h={30} /></td>
    </tr>
  ));

const STATUS_CONFIG = {
  active:             { label: "Active",       className: "badge bg-success" },
  past_due:           { label: "Past Due",     className: "badge bg-warning text-dark" },
  canceled:           { label: "Canceled",     className: "badge bg-danger" },
  incomplete:         { label: "Incomplete",   className: "badge bg-secondary" },
  incomplete_expired: { label: "Expired",      className: "badge bg-danger" },
  trialing:           { label: "Trial",        className: "badge bg-info" },
  unpaid:             { label: "Unpaid",       className: "badge bg-danger" },
  paused:             { label: "Paused",       className: "badge bg-secondary" },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "badge bg-secondary" };
  return <span className={cfg.className}>{cfg.label}</span>;
};

const FaxNumberSubscriptions = ({
  user,
  actionGetFaxNumberSubscriptions,
  actionCancelSubscription,
  actionReactivateSubscription,
}) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cancel modal state
  const [cancelModal, setCancelModal] = useState({ open: false, subId: null, number: "" });
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [cancelLoader, setCancelLoader] = useState(false);

  // Reactivate loader per subscription
  const [reactivating, setReactivating] = useState(null);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await actionGetFaxNumberSubscriptions();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch {
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const openCancelModal = (subId, number) => {
    setCancelAtPeriodEnd(true);
    setCancelModal({ open: true, subId, number });
  };

  const handleCancel = async () => {
    setCancelLoader(true);
    try {
      await actionCancelSubscription(cancelModal.subId, cancelAtPeriodEnd);
      setCancelModal({ open: false, subId: null, number: "" });
      await loadSubscriptions();
    } catch {
      // toast shown by action
    } finally {
      setCancelLoader(false);
    }
  };

  const handleReactivate = async (subId) => {
    setReactivating(subId);
    try {
      await actionReactivateSubscription(subId);
      await loadSubscriptions();
    } catch {
      // toast shown by action
    } finally {
      setReactivating(null);
    }
  };

  return (
    <div className="main-content">
      <style>{skeletonStyle}</style>
      <div className="page-content">
        <div className="container-fluid">
          {/* Page Header */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-0 main_heading">Fax Number Subscriptions</h4>
                  <p className="text-muted mb-0 mt-1" style={{ fontSize: 13 }}>
                    Manage recurring subscriptions for your fax numbers
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Link to="/buy-fax-phone-numbers" className="btn btn-primary btn-sm">
                    <i className="bx bx-plus me-1" />Buy New Number
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <div className="card" style={{ borderRadius: 12, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead style={{ background: "#f8f9fa" }}>
                          <tr>
                            <th className="ps-4 py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Phone Number</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Status</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Monthly Fee</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Next Billing</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Renewal</th>
                            <th className="pe-4 py-3 text-end" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody><SkeletonRows /></tbody>
                      </table>
                    </div>
                  ) : subscriptions.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        padding: "56px 24px",
                      }}
                    >
                      <div
                        style={{
                          width: 72,
                          height: 72,
                          borderRadius: "50%",
                          background: "#eef0fb",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 18,
                        }}
                      >
                        <i className="bx bx-phone" style={{ fontSize: 34, color: "#556ee6" }} />
                      </div>
                      <h5 className="fw-semibold mb-2" style={{ color: "#343a40" }}>
                        No active fax number subscriptions
                      </h5>
                      <p className="text-muted mb-4" style={{ fontSize: 14, maxWidth: 380 }}>
                        Purchase your first fax number to start sending and receiving faxes.
                      </p>
                      <Link to="/buy-fax-phone-numbers" className="btn btn-primary px-4">
                        <i className="bx bx-plus me-1" />Buy a Fax Number
                      </Link>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead style={{ background: "#f8f9fa" }}>
                          <tr>
                            <th className="ps-4 py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Phone Number</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Status</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Monthly Fee</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Next Billing</th>
                            <th className="py-3" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Renewal</th>
                            <th className="pe-4 py-3 text-end" style={{ fontWeight: 600, fontSize: 13, color: "#495057" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subscriptions.map((sub) => {
                            const isCanceling = sub.cancel_at !== null && sub.status === "active";
                            const faxNum = sub.faxNumber;
                            return (
                              <tr key={sub.subscription_id}>
                                <td className="ps-4">
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      style={{
                                        width: 36, height: 36, borderRadius: "50%",
                                        background: "#f0f2ff", display: "flex",
                                        alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                      }}
                                    >
                                      <i className="bx bx-phone" style={{ color: "#556ee6", fontSize: 18 }} />
                                    </div>
                                    <div>
                                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                                        {faxNum?.number ?? "—"}
                                      </div>
                                      {faxNum?.number_area_code && (
                                        <small className="text-muted">Area {faxNum.number_area_code}</small>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <StatusBadge status={sub.status} />
                                  {isCanceling && (
                                    <div>
                                      <small className="text-warning d-block mt-1" style={{ fontSize: 11 }}>
                                        Cancels {moment(sub.cancel_at).format("MMM D, YYYY")}
                                      </small>
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {sub.is_free ? (
                                    <span className="fw-medium text-success">
                                      $0.00<small className="text-muted">/mo</small>
                                      <span className="badge bg-success ms-2">FREE</span>
                                    </span>
                                  ) : (
                                    <span className="fw-medium">
                                      ${parseFloat(sub.monthly_amount ?? faxNum?.number_monthly_fees ?? 6.99).toFixed(2)}
                                      <small className="text-muted">/mo</small>
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {sub.current_period_end ? (
                                    <span style={{ fontSize: 13 }}>
                                      {moment(sub.current_period_end).format("MMM D, YYYY")}
                                    </span>
                                  ) : "—"}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${isCanceling ? "bg-warning text-dark" : "bg-light text-dark"}`}
                                    style={{ fontSize: 11 }}
                                  >
                                    {isCanceling ? "Scheduled to cancel" : "Auto-renews"}
                                  </span>
                                </td>
                                <td className="pe-4 text-end">
                                  {sub.status !== "canceled" && faxNum?.number_ID && (
                                    <Link
                                      to={`/phone-number-set-route/${faxNum.number_ID}`}
                                      className="btn btn-sm btn-outline-primary me-2"
                                      title="Configure Route"
                                    >
                                      <i className="bx bx-cog me-1" />Configure Route
                                    </Link>
                                  )}
                                  {isCanceling ? (
                                    <button
                                      className="btn btn-sm btn-outline-success"
                                      onClick={() => handleReactivate(sub.stripe_subscription_id)}
                                      disabled={reactivating === sub.stripe_subscription_id}
                                    >
                                      {reactivating === sub.stripe_subscription_id ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        <><i className="bx bx-refresh me-1" />Reactivate</>
                                      )}
                                    </button>
                                  ) : sub.status === "active" ? (
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => openCancelModal(sub.stripe_subscription_id, faxNum?.number)}
                                    >
                                      <i className="bx bx-x me-1" />Cancel
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {cancelModal.open && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: 12 }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-semibold">Cancel Subscription</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setCancelModal({ open: false, subId: null, number: "" })}
                />
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-3">
                  Cancel subscription for <strong>{cancelModal.number}</strong>?
                </p>
                <div className="mb-3">
                  <div
                    className={`p-3 rounded cursor-pointer mb-2 ${cancelAtPeriodEnd ? "border border-primary" : "border"}`}
                    style={{ cursor: "pointer", background: cancelAtPeriodEnd ? "#f0f2ff" : "#fff" }}
                    onClick={() => setCancelAtPeriodEnd(true)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <input type="radio" checked={cancelAtPeriodEnd} onChange={() => setCancelAtPeriodEnd(true)} />
                      <div>
                        <div className="fw-medium">Cancel at period end</div>
                        <small className="text-muted">Number stays active until billing cycle ends</small>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`p-3 rounded cursor-pointer ${!cancelAtPeriodEnd ? "border border-danger" : "border"}`}
                    style={{ cursor: "pointer", background: !cancelAtPeriodEnd ? "#fff5f5" : "#fff" }}
                    onClick={() => setCancelAtPeriodEnd(false)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <input type="radio" checked={!cancelAtPeriodEnd} onChange={() => setCancelAtPeriodEnd(false)} />
                      <div>
                        <div className="fw-medium text-danger">Cancel immediately</div>
                        <small className="text-muted">Number is deactivated right away, no refund</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setCancelModal({ open: false, subId: null, number: "" })}
                >
                  Keep Subscription
                </button>
                <button
                  type="button"
                  className={`btn ${cancelAtPeriodEnd ? "btn-warning" : "btn-danger"}`}
                  onClick={handleCancel}
                  disabled={cancelLoader}
                >
                  {cancelLoader ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : null}
                  {cancelAtPeriodEnd ? "Schedule Cancellation" : "Cancel Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  actionGetFaxNumberSubscriptions,
  actionCancelSubscription,
  actionReactivateSubscription,
})(FaxNumberSubscriptions);
